import React, { useState } from 'react';
import { ArrowLeft, TrendingUp, Shield, Users, Zap, CheckCircle, Mail, Calendar, BookOpen } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const Investors = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    investmentFocus: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/api/contact`, {
        ...formData,
        type: 'investor_inquiry'
      });
      
      toast.success('Thank you! We\'ll be in touch soon.');
      setFormData({
        name: '',
        email: '',
        organization: '',
        investmentFocus: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Something went wrong. Please try emailing us directly at hello@westbay.io');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="investors-page">
      {/* Hero Section */}
      <section className="investors-hero">
        <div className="investors-container">
          <button onClick={() => navigate('/')} className="back-to-home">
            <ArrowLeft size={20} />
            <span>Back to Home</span>
          </button>

          <div className="investors-hero-content">
            <div className="logo-text">WestBay</div>
            <h1 className="investors-title">
              <BookOpen size={48} style={{display: 'inline-block', marginRight: '16px', verticalAlign: 'middle'}} />
              Our Story
            </h1>
            <p className="investors-subtitle">
              The authentic journey behind Poser AI™ and WestBay
            </p>
            
            <div className="investors-badges">
              <div className="investor-badge">
                <span className="patent-text">Patent Pending</span> • Filed Nov 15, 2025
              </div>
              <div className="investor-badge">
                <span className="trademark-text">Trademark Filed</span> • Poser AI™
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Founder Story - Magazine Style */}
      <section className="magazine-article">
        <div className="magazine-container">
          {/* Magazine Hero - 90s Rolling Stone Grunge Style */}
          <div className="magazine-hero">
            <img src="/jim-kelce-founding-day.jpg" alt="Jim Bukowski and Kelce - Founding Day" className="magazine-hero-image" />
          </div>
          <div className="image-caption">
            This photo was taken on Founding Day — Saturday, November 15th, 2025, the exact day Poser AI™ was born and Kelce's birthday.
            <br />— Jim Bukowski, Founder
          </div>

          {/* Opening Letter */}
          <div className="magazine-opening-letter">
            <div className="magazine-author-note">
              <em>"Founding Day wasn't fancy. It was authentic — and that's exactly why it means everything to me."</em>
            </div>
            <div className="drop-cap-container">
              <span className="drop-cap">I</span>
              <p className="opening-text">
                'm originally from Philadelphia — and if you know anything about Philly, you know how deep the passion runs. The Birds, the energy, the grit, the underdog spirit — it shaped me. That city made me tough, made me real, and taught me that heart matters as much as talent.
              </p>
            </div>
          </div>
          
          {/* Magazine Body */}
          <div className="magazine-body">
            <div className="magazine-content">
              <p className="magazine-paragraph">
                And yeah, I'm still that guy in Florida wearing an Iverson hoodie.
              </p>

              <p className="magazine-paragraph">
                Philly pride shows up in my life every day… even in the form of my corgi, <strong>Kelce</strong> — named after one of the greatest Eagles of all time. He's my partner, my shadow, and has been with me through the highest highs and the darkest lows.
              </p>

              <p className="magazine-paragraph">
                I was sitting outside a small breakfast spot in Florida, wearing my trusty Iverson 3 hoodie, Kelce staring up at me like he always does, waiting for a piece of toast.
              </p>

              <p className="magazine-paragraph">
                It wasn't a corporate boardroom moment.<br />
                It wasn't champagne and investors.<br />
                It was just <strong>me, my dog, my breakfast, and the belief that it was finally time to bet on myself.</strong>
              </p>

              <h2 className="magazine-subhead">Built on Real Life, Real Resilience</h2>

              <p className="magazine-paragraph">
                That's what Poser AI™ is built on — not ego, not flash — but real life, real resilience, and real American grit.
              </p>

              <p className="magazine-paragraph">
                Just like Philly.<br />
                Just like the Navy.<br />
                Just like everything I've overcome to get here.
              </p>

              <div className="magazine-pullquote pullquote-center">
                <div className="pullquote-text-large">Founding Day wasn't fancy.<br />It was authentic.</div>
              </div>

              <h2 className="magazine-subhead">The Journey to Founding Day</h2>

              <p className="magazine-paragraph">
                I didn't come from privilege. I grew up homeless and dropped out of high school because surviving each day was the only priority. But I never stopped showing up.
              </p>

              <p className="magazine-paragraph">
                I joined the U.S. Navy, earned my GED, and served through September 11th — a period that shaped my values and identity forever. <em>Honor, Courage, and Commitment</em> weren't just slogans; they became the structure I built my entire life on.
              </p>

              <h2 className="magazine-subhead">Self-Taught Excellence</h2>

              <p className="magazine-paragraph">
                Professionally, I taught myself everything. <strong>I didn't have a degree. No one handed me a path.</strong>
              </p>

              <p className="magazine-paragraph magazine-emphasis">
                I earned it.
              </p>

              <div className="magazine-fact-box">
                <h3 className="fact-box-title">Credentials</h3>
                <ul className="fact-box-list">
                  <li>5× AWS Certified</li>
                  <li>AWS Security Specialty</li>
                  <li>AWS SysOps Administrator</li>
                  <li>Terraform Certified</li>
                  <li>Cloud, DevOps, and security specialist</li>
                  <li>Architect behind platforms with real consequences</li>
                </ul>
              </div>

              <p className="magazine-paragraph">
                I've supported and secured some of the most demanding industries in the world: payment gateways, internet gambling platforms, PCI-regulated environments, life sciences infrastructure for Big Pharma, cloud-native MES platforms, and AI-enabled DevOps automation.
              </p>

              <div className="magazine-stat-highlight">
                <div className="stat-number">$5M+</div>
                <div className="stat-description">saved at his last role by optimizing AWS environment and cloud architecture</div>
              </div>

              <div className="magazine-section-break">⁂</div>

              <h2 className="magazine-subhead">The Breaking Point</h2>

              <p className="magazine-paragraph">
                Then my life shifted.
              </p>

              <p className="magazine-paragraph">
                Shortly after <strong>losing my mother</strong>, while still processing grief, I experienced an unexpected separation from my previous role. Out of respect for agreements, I won't share details. What I will say is that it came during the hardest emotional period of my life.
              </p>

              <div className="magazine-pullquote pullquote-center">
                <div className="pullquote-text-large">It broke me.<br />But it also revealed me.</div>
              </div>

              <p className="magazine-paragraph">
                I realized I had outgrown the space I was in. I realized I had spent years building someone else's dream instead of my own. And I knew it was time to build a life on my terms.
              </p>

              <h2 className="magazine-subhead">The Rebirth</h2>

              <p className="magazine-paragraph magazine-emphasis">
                So I bet on myself.
              </p>

              <p className="magazine-paragraph">
                I relocated to Florida. I secured financial independence through a strategic STR acquisition. I rebuilt my foundation, my confidence, and my purpose. And then I created my company.
              </p>

              <p className="magazine-paragraph">
                Inspired by the expressive brilliance of Vincent van Gogh — someone I resemble in my own modern tech-world way — I built <strong>Poser AI™</strong>, a patent-pending platform under <strong>The WestBay Group</strong> that merges military discipline, artistic intuition, practical cloud expertise, American innovation, AI intelligence, and rugged resilience. I believe technology should empower people without ego or complexity — the same reason I admire brands like Cadillac and drive the Lyriq. Good tech should elevate your life effortlessly.
              </p>

              <p className="magazine-paragraph">
                <strong>Poser AI™ carries that philosophy:</strong> Expressive. Intelligent. Human. Security and automation that feel natural, not overwhelming.
              </p>

              <div className="magazine-finale">
                <p className="finale-text">
                  All of it self-funded.<br />
                  All of it self-built.<br />
                  All of it earned through a lifetime of showing up.
                </p>
                <p className="finale-text">
                  This isn't revenge. This isn't reaction.<br />
                  <strong>This is reinvention.</strong>
                </p>
                <p className="finale-closing">
                  This is the American dream — lived in real time.<br />
                  And I'm just getting started.
                </p>
              </div>
            </div>

            {/* Magazine Sidebar */}
            <aside className="magazine-sidebar">
              <div className="sidebar-section">
                <h3 className="sidebar-title">At a Glance</h3>
                <div className="sidebar-item">
                  <div className="sidebar-label">Company</div>
                  <div className="sidebar-value">The WestBay Group</div>
                </div>
                <div className="sidebar-item">
                  <div className="sidebar-label">Product</div>
                  <div className="sidebar-value">Poser AI™</div>
                </div>
                <div className="sidebar-item">
                  <div className="sidebar-label">Founded</div>
                  <div className="sidebar-value">November 15, 2025</div>
                  <div className="sidebar-detail">Founding Day 🐾</div>
                </div>
                <div className="sidebar-item">
                  <div className="sidebar-label">Location</div>
                  <div className="sidebar-value">Tampa, FL</div>
                </div>
                <div className="sidebar-item">
                  <div className="sidebar-label">Status</div>
                  <div className="sidebar-value">Self-Funded</div>
                </div>
              </div>

              <div className="sidebar-section">
                <h3 className="sidebar-title">IP Protection</h3>
                <div className="sidebar-item">
                  <div className="sidebar-label">Patent</div>
                  <div className="sidebar-value">Pending</div>
                  <div className="sidebar-detail">Filed Nov 15, 2025</div>
                </div>
                <div className="sidebar-item">
                  <div className="sidebar-label">Trademark</div>
                  <div className="sidebar-value">Filed</div>
                  <div className="sidebar-detail">USPTO Nov 15, 2025</div>
                </div>
              </div>

              <div className="sidebar-section">
                <h3 className="sidebar-title">Seeking Investment</h3>
                <div className="sidebar-item">
                  <div className="sidebar-label">Round</div>
                  <div className="sidebar-value">Seed/Angel</div>
                </div>
                <div className="sidebar-item">
                  <div className="sidebar-label">Amount</div>
                  <div className="sidebar-value">$500K - $750K</div>
                </div>
                <div className="sidebar-item">
                  <div className="sidebar-label">Use</div>
                  <div className="sidebar-value">Product, GTM, Team</div>
                </div>
              </div>

              <div className="sidebar-cta">
                <a href="#contact" className="sidebar-button">Request Materials</a>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Why Invest */}
      <section className="investors-section why-invest-section">
        <div className="investors-container">
          <h2 className="section-title">Why Invest in Poser AI™</h2>
          
          <div className="why-invest-grid">
            <div className="invest-card">
              <CheckCircle className="invest-icon" />
              <h3>Founder-Market Fit</h3>
              <p>
                Built exactly what Jim wished existed. He's lived the problem for years in 
                PCI payments, gambling, pharma, and life sciences.
              </p>
            </div>

            <div className="invest-card">
              <CheckCircle className="invest-icon" />
              <h3>Self-Funded Commitment</h3>
              <p>
                Zero dilution to date. Founder relocated, created cash flow through real estate, 
                and reinvested everything. That's skin in the game.
              </p>
            </div>

            <div className="invest-card">
              <CheckCircle className="invest-icon" />
              <h3>IP Protection</h3>
              <p>
                Patent pending and trademark filed (Nov 15, 2025) BEFORE raising capital. 
                This is a founder who thinks long-term.
              </p>
            </div>

            <div className="invest-card">
              <CheckCircle className="invest-icon" />
              <h3>Technical Credibility</h3>
              <p>
                Full-stack platform already built: AWS Bedrock, Claude 4.5, React, FastAPI, 
                MongoDB, Terraform. Not just ideas—shipping code.
              </p>
            </div>

            <div className="invest-card">
              <CheckCircle className="invest-icon" />
              <h3>Unique Positioning</h3>
              <p>
                "Less scary" approach with Van Gogh branding breaks enterprise security stereotypes. 
                Highly memorable, emotionally resonant.
              </p>
            </div>

            <div className="invest-card">
              <CheckCircle className="invest-icon" />
              <h3>Proven Resilience</h3>
              <p>
                Rebuilt from breakdown. That resilience translates to navigating startup challenges, 
                pivoting when needed, and persisting through hard problems.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Market Opportunity */}
      <section className="investors-section market-section">
        <div className="investors-container">
          <h2 className="section-title">Market Opportunity</h2>
          
          <div className="market-stats">
            <div className="stat-card">
              <div className="stat-number">$200B+</div>
              <div className="stat-label">Global Cloud Security Market (2025)</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">60%</div>
              <div className="stat-label">SMBs Lack Dedicated Security</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">80%</div>
              <div className="stat-label">Breaches from Misconfiguration</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">$400/hr</div>
              <div className="stat-label">Security Consulting Costs</div>
            </div>
          </div>

          <div className="market-description">
            <h3>The Problem</h3>
            <p>
              Cloud security is overwhelming. SMBs can't afford consultants. Enterprise tools are 
              too complex. Security talent is scarce (6+ months to hire). Real-time guidance is 
              limited to business hours.
            </p>
            
            <h3>Our Solution</h3>
            <p>
              Poser AI™ is a 24/7 cloud solutions architect powered by AWS Bedrock and Claude 4.5. 
              It provides instant security, DevOps, and AI integration guidance—backed by human 
              experts when needed. Approachable design. Enterprise credibility.
            </p>
          </div>
        </div>
      </section>

      {/* Traction */}
      <section className="investors-section traction-section">
        <div className="investors-container">
          <h2 className="section-title">Traction & Milestones</h2>
          
          <div className="traction-timeline">
            <div className="timeline-item completed">
              <div className="timeline-marker"></div>
              <div className="timeline-content">
                <h3>✅ MVP Complete</h3>
                <p>Full-stack platform deployed with AWS Bedrock, multi-model AI, authentication, and admin dashboard.</p>
              </div>
            </div>

            <div className="timeline-item completed">
              <div className="timeline-marker"></div>
              <div className="timeline-content">
                <h3>✅ IP Protection Filed</h3>
                <p>Patent application and trademark filed with USPTO (November 15, 2025).</p>
              </div>
            </div>

            <div className="timeline-item completed">
              <div className="timeline-marker"></div>
              <div className="timeline-content">
                <h3>✅ Legal Framework</h3>
                <p>Comprehensive Terms of Use, IP Policy, and DMCA procedures established.</p>
              </div>
            </div>

            <div className="timeline-item current">
              <div className="timeline-marker"></div>
              <div className="timeline-content">
                <h3>🔵 Seeking Angel Investment</h3>
                <p>$500K-$750K to scale platform, expand features, and acquire first paying customers.</p>
              </div>
            </div>

            <div className="timeline-item future">
              <div className="timeline-marker"></div>
              <div className="timeline-content">
                <h3>🎯 Q1 2026 Goals</h3>
                <p>First paying customers, product-market fit validation, AWS Partner Network application.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Ask */}
      <section className="investors-section ask-section">
        <div className="investors-container">
          <h2 className="section-title">The Ask</h2>
          
          <div className="ask-content">
            <div className="ask-details">
              <h3>Raising: $500K - $750K</h3>
              <p className="ask-stage">Seed/Angel Round</p>
              
              <div className="use-of-funds">
                <h4>Use of Funds</h4>
                <ul>
                  <li><strong>40%</strong> - Product Development (features, integrations, mobile)</li>
                  <li><strong>30%</strong> - Go-to-Market (marketing, sales, partnerships)</li>
                  <li><strong>20%</strong> - Team Expansion (engineers, sales, support)</li>
                  <li><strong>10%</strong> - Operations (legal, compliance, infrastructure)</li>
                </ul>
              </div>

              <div className="milestones">
                <h4>Milestones This Round Will Achieve</h4>
                <ul>
                  <li>First 100 paying customers</li>
                  <li>$10K MRR (Monthly Recurring Revenue)</li>
                  <li>Product-market fit validation</li>
                  <li>AWS Partner Network membership</li>
                  <li>Team of 3-5 (engineering, sales)</li>
                  <li>Positioned for Series A ($2M-$5M)</li>
                </ul>
              </div>
            </div>

            <div className="ask-sidebar">
              <div className="investor-fit">
                <h4>Ideal Investor Profile</h4>
                <ul>
                  <li>Angel investors or micro-VCs</li>
                  <li>Cloud security experience</li>
                  <li>B2B SaaS background</li>
                  <li>Hands-on mentorship</li>
                  <li>Network access (AWS, customers)</li>
                  <li>Believes in founder-led companies</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="investors-section contact-section">
        <div className="investors-container">
          <h2 className="section-title">Let's Talk</h2>
          
          <div className="contact-grid">
            <div className="contact-info">
              <h3>Ready to Learn More?</h3>
              <p>
                We're looking for strategic investors who understand the cloud security space 
                and can help us scale.
              </p>
              
              <div className="contact-methods">
                <div className="contact-method">
                  <Mail size={24} />
                  <div>
                    <strong>Email</strong>
                    <p>hello@westbay.io</p>
                  </div>
                </div>
                
                <div className="contact-method">
                  <Calendar size={24} />
                  <div>
                    <strong>Schedule a Call</strong>
                    <p>Submit the form and we'll send you a calendar link</p>
                  </div>
                </div>
              </div>

              <div className="contact-note">
                <p>
                  <strong>Materials Available:</strong><br />
                  • Full pitch deck<br />
                  • Financial projections<br />
                  • Product roadmap<br />
                  • Technical architecture docs
                </p>
              </div>
            </div>

            <form className="investor-contact-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Your full name"
                />
              </div>

              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="your.email@example.com"
                />
              </div>

              <div className="form-group">
                <label>Organization</label>
                <input
                  type="text"
                  name="organization"
                  value={formData.organization}
                  onChange={handleChange}
                  placeholder="Your fund/company"
                />
              </div>

              <div className="form-group">
                <label>Investment Focus</label>
                <select
                  name="investmentFocus"
                  value={formData.investmentFocus}
                  onChange={handleChange}
                >
                  <option value="">Select...</option>
                  <option value="angel">Angel Investor</option>
                  <option value="micro-vc">Micro VC</option>
                  <option value="vc">Venture Capital</option>
                  <option value="strategic">Strategic/Corporate</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label>Message</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="4"
                  placeholder="Tell us about your investment thesis, check size, and what interests you about Poser AI™"
                />
              </div>

              <button type="submit" className="cta-button-primary" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Request Investment Materials'}
              </button>

              <p className="form-disclaimer">
                We respect your privacy. Your information will only be used to share investment 
                materials and schedule conversations.
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-single-line">
          <div className="footer-brand">
            <div className="logo-text">WestBay</div>
          </div>
          
          <div className="footer-center">
            <span className="body-small">© 2026 Westbay Group LLC. All rights reserved.</span>
            <span className="footer-separator">•</span>
            <button onClick={() => navigate('/privacy-policy')} className="footer-link">Privacy</button>
            <span className="footer-separator">•</span>
            <button onClick={() => navigate('/terms-of-service')} className="footer-link">Terms</button>
            <span className="footer-separator">•</span>
            <span className="footer-patent body-small">
              Poser AI™ • <span className="patent-text">Patent Pending</span>
            </span>
          </div>
          
          <button 
            onClick={() => navigate('/admin')} 
            className="footer-shield-icon"
            title="Admin Dashboard"
          >
            <Shield size={24} />
          </button>
        </div>
      </footer>
    </div>
  );
};

export default Investors;
